import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { DeferredSpeedInsights } from "@/components/DeferredSpeedInsights";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CreditsProvider } from "@/hooks/useCredits";
import { useAuth } from "@/hooks/useAuth";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import LoadingScreen from "@/components/LoadingScreen";
// framer-motion is NOT imported here — see DeferredMotionProvider for the
// lazy LazyMotion wrapper that keeps motion-vendor out of the LCP path.
import { DeferredMotionProvider } from "@/components/DeferredMotionProvider";
import { scheduleThirdPartyAnalytics } from "@/thirdPartyAnalytics";

// Eagerly import Studio — it's the homepage and must render fast for FCP
import Studio from "./pages/Studio";

const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Settings = lazy(() => import("./pages/Settings"));
const AICoach = lazy(() => import("./pages/AICoach"));
const NotFound = lazy(() => import("./pages/NotFound"));

const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <main className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </main>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  useEffect(() => {
    scheduleThirdPartyAnalytics();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        <TooltipProvider>
          <Sonner />
          <BrowserRouter>
            <DeferredSpeedInsights />
            <CreditsProvider>
              <DeferredMotionProvider>
                <Suspense fallback={<LoadingScreen />}>
                  <main>
                    <Routes>
                      <Route path="/" element={<Studio />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
                      <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
                      <Route path="/ai-coach" element={<AICoach />} />
                      <Route path="/code" element={<NotFound />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </main>
                </Suspense>
              </DeferredMotionProvider>
            </CreditsProvider>
          </BrowserRouter>
        </TooltipProvider>
      </AppErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
